import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import {
    Container,
    Row,
    Col,
    Card,
    CardHeader,
    CardBody,
    Table,
    Spinner,
    Button,
    FormGroup,
    Label,
    Input,
    FormText
} from 'reactstrap';
import { toast } from 'react-toastify';
import { apiURL1 } from '../../config';
import Header from '../../components/Headers/Header';
import Footer from 'components/Footers/Footer';

const baseURL = apiURL1;

const sumComponentMarks = (compObj, componentList) =>
    componentList.reduce((sum, c) => {
        const val = compObj?.[c._id]?.marks;
        return sum + (val !== '' && val !== undefined && val !== null && !isNaN(val) ? parseFloat(val) : 0);
    }, 0);

const normalizeNumericInput = (value) => {
    if (value === '' || value === null || value === undefined) return '';
    const str = String(value);
    if (!/^\d*\.?\d*$/.test(str)) return null;
    if (str === '.' || str === '') return str;
    if (!str.includes('.')) {
        return str.replace(/^0+/, '') || '0';
    }
    const [intPart, decPart] = str.split('.');
    const normalizedInt = intPart.replace(/^0+/, '') || '0';
    return decPart !== undefined ? `${normalizedInt}.${decPart}` : normalizedInt;
};

function AddSubjectResult() {
    const token = localStorage.getItem("token");

    const { classId, subjectId, examId } = useParams();
    const location = useLocation();
    const navigate = useNavigate();

    const { className, subjectName, teacherName, type, typeId, year } = location.state || {};
    // console.log('Type is ', typeId)

    const [students, setStudents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [totalMarks, setTotalMarks] = useState('');
    const [results, setResults] = useState({});
    const [errors, setErrors] = useState({});
    const [toastDisplayed, setToastDisplayed] = useState(false);
    const [isMid, setIsMid] = useState(false);
    const [components, setComponents] = useState([]);
    const [componentTotals, setComponentTotals] = useState({});

    useEffect(() => {
        fetchExamDetails();
    }, [examId]);

    useEffect(() => {
        fetchData();
    }, [classId, subjectId, type, year]);

    useEffect(() => {
        if (isMid && components.length > 0 && students.length > 0) {
            setResults(prev => {
                const next = { ...prev };
                students.forEach(stu => {
                    const sid = stu._id;
                    next[sid] = next[sid] || {};
                    if (!next[sid].components) {
                        const compObj = {};
                        components.forEach(c => {
                            compObj[c._id] = { marks: '' };
                        });
                        next[sid].components = compObj;
                    } else {
                        components.forEach(c => {
                            if (!next[sid].components[c._id]) {
                                next[sid].components[c._id] = { marks: '' };
                            }
                        });
                    }
                });
                return next;
            });
            // Initialize top-level Other Activities totals if not set
            setComponentTotals(prev => {
                const updated = { ...prev };
                components.forEach(c => {
                    if (updated[c._id] === undefined) {
                        updated[c._id] = '';
                    }
                });
                return updated;
            });
        }
    }, [isMid, components, students]);

    const fetchData = async () => {
        setLoading(true);
        try {
            // Attempt new subject-level route first, then fallback
            let resultsResponse;
            try {
                resultsResponse = await axios.get(
                    `${baseURL}/results/subject/${subjectId}/type/${typeId}/year/${year}/${classId}?exam=${examId}`,
                    { headers: { 'Authorization': `Bearer ${token}` } }
                );
            } catch (primaryErr) {
                resultsResponse = await axios.get(
                    `${baseURL}/superadmin/results/subject/${subjectId}/type/${typeId}/year/${year}/${classId}?exam=${examId}`,
                    { headers: { 'Authorization': `Bearer ${token}` } }
                );
            }

            if (resultsResponse.data?.data && resultsResponse.data.data.length > 0) {
                const resultData = resultsResponse.data.data[0];
                let useMid = isMid;
                if (typeof resultData.isMainMid === 'boolean') {
                    useMid = resultData.isMainMid;
                    setIsMid(useMid);
                    if (useMid) {
                        await fetchComponents();
                    }
                }
                const studentData = Array.isArray(resultData.students) ? resultData.students : [];

                setStudents(studentData.map(s => ({
                    ...s.student,
                    remarks: s.remarks,
                    picture: s.student?.picture
                })));

                if (!useMid) {
                    setTotalMarks(resultData.subjectTotalMarksFinal ?? '');
                }

                const compsMeta = Array.isArray(resultData.components) ? resultData.components : [];
                const normalizedComps = compsMeta.map(comp => ({
                    _id: comp?.component?._id || comp?.componentId,
                    name: comp?.component?.name || comp?.name || 'Component'
                })).filter(c => !!c._id);

                if (normalizedComps.length > 0) {
                    setComponents(normalizedComps);
                }

                // Build initial results per student tied to isMid
                const initialResults = {};
                studentData.forEach(student => {
                    const sid = student.student?._id;
                    if (!sid) return;
                    const compObt = useMid && Array.isArray(student.componentObtained) ? student.componentObtained : [];
                    const compMap = compObt.reduce((acc, co) => {
                        const compId = co?.component?._id || co?.componentId;
                        if (compId) {
                            const marks = co?.marks ?? '';
                            acc[compId] = {
                                marks: marks !== '' && marks !== null && marks !== undefined
                                    ? (normalizeNumericInput(String(marks)) ?? '')
                                    : ''
                            };
                        }
                        return acc;
                    }, {});

                    if (useMid) {
                        const comps = Object.keys(compMap).length > 0 ? compMap : {};
                        initialResults[sid] = {
                            marksObtained: sumComponentMarks(comps, normalizedComps),
                            remarks: student.remarks ?? '',
                            components: comps
                        };
                    } else {
                        initialResults[sid] = {
                            marksObtained: student.subjectObtainMarksFinal ?? '',
                            remarks: student.remarks ?? ''
                        };
                    }
                });
                setResults(initialResults);

                // Seed totals for each component from API
                const totalsSeed = {};
                compsMeta.forEach(comp => {
                    const compId = comp?.component?._id || comp?.componentId;
                    if (compId) {
                        const raw = comp?.totalMarks;
                        totalsSeed[compId] = raw !== undefined && raw !== null && raw !== ''
                            ? (normalizeNumericInput(String(raw)) ?? '')
                            : '';
                    }
                });
                if (Object.keys(totalsSeed).length > 0) setComponentTotals(prev => ({ ...prev, ...totalsSeed }));

                setLoading(false);
                return; // Data found, exit the function
            }
        } catch (error) {
            console.error('Error fetching results:', error);
            toast.error('Error fetching results. Trying to fetch student list.');
        }

        // If primary API fails or returns no data, fetch student list
        try {
            const studentResponse = await axios.get(`${baseURL}/superadmin/getStudentsByClass/${classId}`, {
                headers: {
                    'Authorization': `Bearer ${token}`, // Include the token in the headers
                }
            });
            setStudents(studentResponse.data.data.map(student => ({
                ...student,
                marksObtained: '',
                // grade: '',
                remarks: '',
                picture: student.picture // Include picture here as well
            })));
            setTotalMarks('');
            const initialResults = {};
            studentResponse.data.data.forEach(student => {
                initialResults[student._id] = {
                    marksObtained: '',
                    remarks: '',
                    // grade: ''
                };
            });
            setResults(initialResults);
        } catch (error) {
            console.error('Error fetching students:', error);
            toast.error('Error fetching students.');
        } finally {
            setLoading(false);
        }
    };

    const fetchExamDetails = async () => {
        try {
            const response = await axios.get(`${baseURL}/superadmin/exams/${examId}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const exam = response.data?.data;
            const mid = !!exam?.isMid;
            setIsMid(mid);
            if (mid) {
                await fetchComponents();
            }
        } catch (error) {
            console.error('Error fetching exam:', error);
        }
    };

    const fetchComponents = async () => {
        try {
            const response = await axios.get(`${baseURL}/superadmin/exam-components`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = Array.isArray(response.data?.data) ? response.data.data : [];
            setComponents(data);
            // Initialize missing top-level component totals without overwriting prefilled values
            setComponentTotals(prev => {
                const updated = { ...prev };
                data.forEach(c => {
                    if (updated[c._id] === undefined || updated[c._id] === null) {
                        updated[c._id] = '';
                    }
                });
                return updated;
            });
        } catch (error) {
            console.error('Error fetching exam components:', error);
            toast.error(error.response?.data?.message || 'Error fetching exam components');
            setComponents([]);
        }
    };

    const handleInputChange = (studentId, field, value) => {
        let parsedValue = value;

        if (field === 'marksObtained') {
            console.log('value is ', value)
            const numericValue = Number(value); // convert to number
            parsedValue = Math.max(0, isNaN(numericValue) ? 0 : numericValue);
            console.log('parsedValue is ', parsedValue)

        }

        setResults(prevResults => ({
            ...prevResults,
            [studentId]: {
                ...prevResults[studentId],
                [field]: parsedValue
            }
        }));
    };

    const handleComponentChange = (studentId, componentId, field, value) => {
        if (field !== 'marks') {
            setResults(prev => {
                const next = { ...prev };
                next[studentId] = next[studentId] || {};
                next[studentId].components = next[studentId].components || {};
                next[studentId].components[componentId] = next[studentId].components[componentId] || { marks: '' };
                next[studentId].components[componentId][field] = value;
                return next;
            });
            return;
        }

        if (value === '') {
            setResults(prev => {
                const next = { ...prev };
                next[studentId] = next[studentId] || {};
                next[studentId].components = next[studentId].components || {};
                next[studentId].components[componentId] = { marks: '' };
                if (isMid) {
                    next[studentId].marksObtained = sumComponentMarks(next[studentId].components, components);
                }
                return next;
            });
            return;
        }

        const normalized = normalizeNumericInput(value);
        if (normalized === null) return;

        setResults(prev => {
            const next = { ...prev };
            next[studentId] = next[studentId] || {};
            next[studentId].components = next[studentId].components || {};
            next[studentId].components[componentId] = { marks: normalized };
            if (isMid) {
                next[studentId].marksObtained = sumComponentMarks(next[studentId].components, components);
            }
            return next;
        });
    };

    const handleComponentTotalChange = (componentId, value) => {
        if (value === '') {
            setComponentTotals(prev => ({ ...prev, [componentId]: '' }));
            return;
        }
        const normalized = normalizeNumericInput(value);
        if (normalized === null) return;
        setComponentTotals(prev => ({ ...prev, [componentId]: normalized }));
    };


    const validate = () => {
        let isValid = true;
        const newErrors = {};
        let errorMessage = null;

        // Subject total marks validation (final exam only)
        if (!isMid) {
            if (!totalMarks && totalMarks !== 0) {
                newErrors.totalMarks = 'Total marks are required';
                errorMessage = 'Total marks are required';
                isValid = false;
            } else if (isNaN(totalMarks)) {
                newErrors.totalMarks = 'Total marks must be a number';
                errorMessage = 'Total marks must be a number';
                isValid = false;
            }

            Object.keys(results).forEach(studentId => {
                const marksObtained = results[studentId]?.marksObtained;
                if (marksObtained === undefined || marksObtained === null || marksObtained === '') {
                    newErrors[studentId] = { ...newErrors[studentId], marksObtained: 'Obtained marks are required' };
                    errorMessage = 'Obtained marks are required';
                    isValid = false;
                } else if (isNaN(marksObtained)) {
                    newErrors[studentId] = { ...newErrors[studentId], marksObtained: 'Obtained marks must be a number' };
                    isValid = false;
                } else if (parseFloat(marksObtained) > parseFloat(totalMarks)) {
                    newErrors[studentId] = { ...newErrors[studentId], marksObtained: 'Obtained marks cannot be greater than total marks' };
                    errorMessage = 'Obtained marks cannot be greater than total marks';
                    isValid = false;
                }
            });
        }

        if (isMid) {
            // Mid-term: require component totals and obtained per student
            if (!components.length) {
                errorMessage = 'No mid-term components are configured.';
                isValid = false;
            }
            components.forEach(c => {
                const total = componentTotals[c._id];
                if (total === undefined || total === null || total === '') {
                    newErrors[`compTotal-${c._id}`] = `Total marks required for ${c.name}`;
                    errorMessage = `Total marks required for ${c.name}`;
                    isValid = false;
                } else if (isNaN(total)) {
                    newErrors[`compTotal-${c._id}`] = `Total marks must be a number for ${c.name}`;
                    isValid = false;
                }
            });

            Object.keys(results).forEach(studentId => {
                const compObj = results[studentId]?.components || {};
                components.forEach(c => {
                    const val = compObj[c._id]?.marks;
                    if (val === undefined || val === null || val === '') {
                        newErrors[studentId] = { ...newErrors[studentId], [`comp-${c._id}`]: `Marks required for ${c.name}` };
                        errorMessage = `Marks required for ${c.name}`;
                        isValid = false;
                    } else if (isNaN(val)) {
                        newErrors[studentId] = { ...newErrors[studentId], [`comp-${c._id}`]: `Marks must be a number for ${c.name}` };
                        isValid = false;
                    } else if (componentTotals[c._id] !== '' && !isNaN(componentTotals[c._id]) && parseFloat(val) > parseFloat(componentTotals[c._id])) {
                        newErrors[studentId] = { ...newErrors[studentId], [`comp-${c._id}`]: `Obtained cannot exceed total for ${c.name}` };
                        errorMessage = `Obtained cannot exceed total for ${c.name}`;
                        isValid = false;
                    }
                });
            });
        }

        setErrors(newErrors);

        if (errorMessage && !toastDisplayed) {
            toast.error(errorMessage);
            setToastDisplayed(true);
        }

        return isValid;
    };

    const handleSubmit = async () => {
        setToastDisplayed(false);

        if (validate()) {
            setLoading(true);
            try {
                const resultsArray = students.map(student => {
                    const base = {
                        studentId: student._id,
                        studentName: student.fullName,
                        remarks: results[student._id]?.remarks
                    };
                    if (isMid) {
                        const compArray = components.map(c => ({
                            componentId: c._id,
                            marks: parseFloat(results[student._id]?.components?.[c._id]?.marks || 0),
                            totalMarks: componentTotals[c._id] !== '' && componentTotals[c._id] !== undefined
                                ? parseFloat(componentTotals[c._id])
                                : undefined
                        }));
                        return {
                            ...base,
                            components: compArray,
                            marksObtained: 0
                        };
                    }
                    return {
                        ...base,
                        marksObtained: parseFloat(results[student._id]?.marksObtained || 0)
                    };
                });

                const payload = {
                    examId: examId,
                    subjectId: subjectId,
                    classId: classId,
                    results: resultsArray,
                    ...(isMid ? {} : { totalMarks: totalMarks !== '' ? parseFloat(totalMarks) : undefined })
                };

                // Try new bulk-upload route, fallback to legacy
                try {
                    await axios.post(`${baseURL}/superadmin/results/bulk-upload`, payload, {
                        headers: { 'Authorization': `Bearer ${token}` }
                    });
                } catch (primaryErr) {
                    await axios.post(`${baseURL}/superadmin/results`, payload, {
                        headers: { 'Authorization': `Bearer ${token}` }
                    });
                }
                toast.success('Results uploaded successfully!');
                navigate(`/admin/subject-result/${examId}`);
            } catch (error) {
                console.error('Error uploading results:', error);
                toast.error('Error uploading results');
            } finally {
                setLoading(false);
            }
        }
    };

    if (loading) {
        return (
            <>
                <Header />
                <Container>
                    <Row>
                        <Col>
                            <div style={{ display: 'flex', justifyContent: 'center' }}>
                                <Spinner color="primary" />
                            </div>
                        </Col>
                    </Row>
                </Container>
            </>
        );
    }

    return (
        <>
            <Header />
            <style>
                {`
                    .custom-scrollbar::-webkit-scrollbar {
                        height: 14px;
                        width: 14px;
                    }
                    .custom-scrollbar::-webkit-scrollbar-track {
                        background: #f1f1f1;
                        border-radius: 7px;
                    }
                    .custom-scrollbar::-webkit-scrollbar-thumb {
                        background: #c1c1c1; 
                        border-radius: 7px;
                        border: 3px solid #f1f1f1;
                    }
                    .custom-scrollbar::-webkit-scrollbar-thumb:hover {
                        background: #a8a8a8; 
                    }
                `}
            </style>
            <Container>
                <Row>
                    <Col>
                        <Card>
                            <CardHeader style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <h3>Add Subject Result</h3>
                                <Button color="primary" onClick={handleSubmit} disabled={loading}>
                                    {loading ? <Spinner size="sm" /> : 'Upload'}
                                </Button>
                            </CardHeader>
                            <CardBody>
                                <h4>Class: {className}</h4>
                                <h4>Subject: {subjectName}</h4>
                                <h4>Staff: {teacherName}</h4>

                                {!isMid && (
                                    <FormGroup>
                                        <Label for="totalMarks">Subject Total Marks</Label>
                                        <Input
                                            type="number"
                                            name="totalMarks"
                                            id="totalMarks"
                                            placeholder="Subject Total Marks"
                                            value={totalMarks}
                                            onChange={(e) => setTotalMarks(e.target.value)}
                                            invalid={!!errors.totalMarks}
                                            min="0.0"
                                        />
                                        {errors.totalMarks && <FormText color="danger">{errors.totalMarks}</FormText>}
                                    </FormGroup>
                                )}

                                {isMid && (
                                    <FormGroup>
                                        <Label>Other Activities Total Marks</Label>
                                        <div className="custom-scrollbar" style={{
                                            display: 'flex',
                                            overflowX: 'auto',
                                            gap: '15px',
                                            paddingBottom: '10px'
                                        }}>
                                            {components.map(c => (
                                                <div key={c._id} style={{ minWidth: '220px', flex: '0 0 auto' }}>
                                                    <Label style={{ fontWeight: 600 }}>{c.name}</Label>
                                                    <Input
                                                        type="text"
                                                        inputMode="decimal"
                                                        name={`componentTotal-${c._id}`}
                                                        id={`componentTotal-${c._id}`}
                                                        placeholder={`Total for ${c.name}`}
                                                        value={componentTotals[c._id] ?? ''}
                                                        onChange={(e) => handleComponentTotalChange(c._id, e.target.value)}
                                                    />
                                                    {errors[`compTotal-${c._id}`] && <FormText color="danger">{errors[`compTotal-${c._id}`]}</FormText>}
                                                </div>
                                            ))}
                                        </div>
                                    </FormGroup>
                                )}

                                <div className="table-responsive custom-scrollbar">
                                    <Table className="table">
                                        <thead>
                                            <tr style={{ backgroundColor: '#2980b9', color: 'white' }}>
                                                <th style={{ minWidth: '70px' }}>Picture</th>
                                                <th style={{ minWidth: '120px' }}>Reg Number</th>
                                                <th style={{ minWidth: '200px' }}>Name</th>
                                                {isMid ? (
                                                    components.map(c => (
                                                        <th key={`th-${c._id}`} style={{ minWidth: '150px' }}>{c.name}</th>
                                                    ))
                                                ) : (
                                                    <th style={{ minWidth: '150px' }}>Obtained Marks</th>
                                                )}
                                                {isMid && (<th style={{ minWidth: '150px' }}>Total Obtained</th>)}
                                                {/* <th>Grade</th> */}
                                                <th style={{ minWidth: '250px' }}>Comments</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {students.map(student => (
                                                <tr key={student._id}>
                                                    <td>
                                                        {student.picture ? (
                                                            <img
                                                                src={student.picture}
                                                                alt="Profile"
                                                                style={{ width: '50px', height: '50px', borderRadius: '50%' }}
                                                            />
                                                        ) : (
                                                            <div style={{
                                                                width: '50px',
                                                                height: '50px',
                                                                borderRadius: '50%',
                                                                backgroundColor: '#ddd',
                                                                display: 'flex',
                                                                justifyContent: 'center',
                                                                alignItems: 'center'
                                                            }}>
                                                                No Image
                                                            </div>
                                                        )}
                                                    </td>
                                                    <td>{student.registrationNumber}</td>
                                                    <td>{student.fullName}</td>
                                                    {isMid ? (
                                                        components.map(c => (
                                                            <td key={`td-${student._id}-${c._id}`}>
                                                                <Input
                                                                    type="text"
                                                                    inputMode="decimal"
                                                                    name={`marks-${student._id}-${c._id}`}
                                                                    id={`marks-${student._id}-${c._id}`}
                                                                    placeholder={`Marks for ${c.name}`}
                                                                    value={results[student._id]?.components?.[c._id]?.marks ?? ''}
                                                                    onChange={(e) => handleComponentChange(student._id, c._id, 'marks', e.target.value)}
                                                                    invalid={!!errors[student._id]?.[`comp-${c._id}`]}
                                                                />
                                                                {errors[student._id]?.[`comp-${c._id}`] && <FormText color="danger">{errors[student._id][`comp-${c._id}`]}</FormText>}
                                                            </td>
                                                        ))
                                                    ) : (
                                                        <td>
                                                            <Input
                                                                type="number"
                                                                name={`marksObtained-${student._id}`}
                                                                id={`marksObtained-${student._id}`}
                                                                placeholder="Enter obtained marks"
                                                                value={results[student._id]?.marksObtained ?? ''}
                                                                onChange={(e) => handleInputChange(student._id, 'marksObtained', e.target.value)}
                                                                invalid={!!errors[student._id]?.marksObtained}
                                                                min="0"
                                                            />
                                                            {errors[student._id]?.marksObtained && <FormText color="danger">{errors[student._id].marksObtained}</FormText>}
                                                        </td>
                                                    )}
                                                    {isMid && (
                                                        <td className="align-middle text-center font-weight-bold">
                                                            {sumComponentMarks(results[student._id]?.components, components) || '-'}
                                                            {(() => {
                                                                const obtained = sumComponentMarks(results[student._id]?.components, components);
                                                                const total = components.reduce((sum, c) => {
                                                                    const t = componentTotals[c._id];
                                                                    return sum + (t !== '' && t !== undefined && !isNaN(t) ? parseFloat(t) : 0);
                                                                }, 0);
                                                                if (total > 0 && obtained > total) {
                                                                    return <FormText color="danger">Exceeds activity total ({total})</FormText>;
                                                                }
                                                                return null;
                                                            })()}
                                                        </td>
                                                    )}
                                                    {/* <td>
                                                        <Input
                                                            type="text"
                                                            name={`grade-${student._id}`}
                                                            id={`grade-${student._id}`}
                                                            placeholder="Enter grade"
                                                            value={results[student._id]?.grade || ''}
                                                            onChange={(e) => handleInputChange(student._id, 'grade', e.target.value)}
                                                        />
                                                    </td> */}
                                                    <td>
                                                        <Input
                                                            type="text"
                                                            name={`remarks-${student._id}`}
                                                            id={`remarks-${student._id}`}
                                                            placeholder="Enter comments"
                                                            value={results[student._id]?.remarks || ''}
                                                            onChange={(e) => handleInputChange(student._id, 'remarks', e.target.value)}
                                                        />
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                </div>
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
                <Footer />
            </Container>
        </>
    );
}

export default AddSubjectResult;
